import * as React from 'react';
import { TextInput } from 'react-native';

import { BLOCK_CODE_DEFAULT_OVERFLOW, type BlockCodeData, code } from '@gitbook/doc-core';
import {
    CodeBlock,
    resolveCodeBlockSyntax,
    themedFonts,
    useCurrentTheme,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, clipboard } from '@gitbook/spine-react';

import { BlockWrapper } from '../common';
import { createCodeSyntaxesPalette, useCodeBlockPaletteFactory } from './palette';

/**
 * Component to render a code block.
 */
export function BlockCode(props: BlockProps): React.ReactElement {
    const { editor, node, children } = props;
    const { readOnly } = editor;
    const data = node.data.toFrozenObject<BlockCodeData>();

    const syntax = resolveCodeBlockSyntax(data.syntax || 'text');
    const hasTitle = typeof data.title === 'string';

    const syntaxButtonRef = React.useRef<HTMLButtonElement>(null);
    const syntaxPaletteHandle = usePaletteHandle(syntaxButtonRef);

    const moreButtonRef = React.useRef<HTMLButtonElement>(null);
    const morePaletteHandle = usePaletteHandle(moreButtonRef);

    // Handler to rename a code block's title
    const onRename = (title: string) => {
        editor.change((change) => {
            change.insertDataByKey(node.key, ['title'], title);
        });
    };

    // Handler to select the block on `Escape` key press when cursor is in the caption.
    // This is needed because the caption is an input so it captures the `Escape` key press event.
    const onCaptionEscPress = () => {
        if (!isBlockRangeSelection(editor.value.selection)) {
            const block = editor.value.document.assertBlock(node.key);
            const selection = BlockRange.create({
                anchorBlock: block.key,
                focusBlock: block.key,
                isFocused: true,
            });
            editor.change((change) => {
                change.selectBlock(selection);
            });
        }
    };

    /** Get the inner content of the code block to copy  */
    const contentToCopy = () => code.getCodeText(node);
    const palette = useCodeBlockPaletteFactory({ nodeKey: node.key });

    return (
        <BlockWrapper {...props} palette={palette}>
            <CodeBlock
                translate={false}
                overflow={data.overflow || BLOCK_CODE_DEFAULT_OVERFLOW}
                lineNumbers={!!data.lineNumbers}
                header={
                    hasTitle ? (
                        <CodeBlockHeader
                            title={data.title}
                            isEditable={!readOnly}
                            onTitleChange={onRename}
                            onCaptionEscPress={onCaptionEscPress}
                        />
                    ) : null
                }
                showToolbar={syntaxPaletteHandle.opened || morePaletteHandle.opened}
                toolbar={
                    <div className="flex gap-1">
                        {readOnly ? null : (
                            <Button
                                ref={syntaxButtonRef}
                                active={syntaxPaletteHandle.opened}
                                kind="secondary"
                                size="small"
                                trailingIcon={Icon.ChevronDown}
                                onPress={() => {
                                    syntaxPaletteHandle.open(
                                        createCodeSyntaxesPalette(editor, node.key)
                                    );
                                }}
                                label={syntax.title}
                            />
                        )}
                        <Button
                            kind="secondary"
                            size="small"
                            label="Copy"
                            leadingIcon={Icon.Copy}
                            iconOnly
                            onPress={() => {
                                clipboard.writeText(contentToCopy());
                            }}
                        />
                        {readOnly ? null : (
                            <Button
                                ref={moreButtonRef}
                                iconOnly
                                leadingIcon={Icon.MoreVertical}
                                kind="secondary"
                                size="small"
                                label="More options"
                                onPress={() => {
                                    morePaletteHandle.open(palette());
                                }}
                            />
                        )}
                    </div>
                }
            >
                {children}
            </CodeBlock>
        </BlockWrapper>
    );
}

/**
 * Render a header to display the selected syntax and the snippet title.
 */
function CodeBlockHeader(props: {
    title?: string;
    isEditable: boolean;
    onTitleChange: (title: string) => void;
    onCaptionEscPress?: () => void;
}): React.ReactElement {
    const { title, isEditable, onTitleChange, onCaptionEscPress } = props;
    const [updatedTitle, setUpdatedTitle] = React.useState(title);
    const [theme, font] = useCurrentTheme(themedFonts);

    React.useEffect(() => {
        setUpdatedTitle(title);
    }, [title]);

    const onBlur = () => {
        onTitleChange(updatedTitle || '');
    };

    const [inputHeight, setInputHeight] = React.useState(20);

    return (
        <div contentEditable={false} className="w-3/4 select-none text-wrap pl-4">
            {!isEditable ? (
                <div className="size-full text-muted text-ui-base">{title}</div>
            ) : (
                <TextInput
                    value={updatedTitle}
                    placeholder="Enter a filename"
                    placeholderTextColor={theme.colors.text.muted}
                    onChangeText={(newTitle) => setUpdatedTitle(newTitle)}
                    style={[
                        {
                            // @ts-ignore
                            userSelect: 'text',
                            width: '100%',
                            height: inputHeight,
                            textAlignVertical: 'top',
                        },
                        font.Body,
                        font.textMuted,
                    ]}
                    onContentSizeChange={(e) => setInputHeight(e.nativeEvent.contentSize.height)}
                    multiline
                    onBlur={onBlur}
                    onKeyPress={(event) => {
                        if (event.nativeEvent.key === 'Escape' && onCaptionEscPress) {
                            onCaptionEscPress();
                        }
                    }}
                />
            )}
        </div>
    );
}
